import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Calendar, User, Check, Trash2, Save, Pencil, X, Clock } from "lucide-react";
import { toast } from "sonner";
import SpellCheckButton from "@/components/SpellCheckButton";

function formatDate(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleDateString("nb-NO", { day: "2-digit", month: "short", year: "numeric" });
  } catch { return iso; }
}

function formatDateTime(iso) {
  if (!iso) return "";
  try {
    const d = new Date(iso);
    return d.toLocaleString("nb-NO", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" });
  } catch { return iso; }
}

/**
 * Detail + edit dialog for a single todo. Lets the user:
 *   - Read/edit the full description
 *   - Set a due date
 *   - Reassign to another member
 *   - Toggle done (with applause handled by the parent)
 *   - Delete
 * Emits `onChanged(updatedTodo|null)` where null means deleted.
 */
export default function TodoDetailsDialog({ open, onOpenChange, todo, members = [], onChanged, onToggleDone }) {
  const [text, setText] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [assignee, setAssignee] = useState("");
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (todo) {
      setText(todo.text || "");
      setDescription(todo.description || "");
      setDueDate(todo.due_date || "");
      setAssignee(todo.assignee_user_id || "");
      setEditing(false);
    }
  }, [todo]);

  if (!todo) return null;

  const isOverdue = todo.due_date && !todo.done && new Date(todo.due_date) < new Date(new Date().setHours(0, 0, 0, 0));

  const save = async () => {
    setSaving(true);
    try {
      const m = members.find((x) => x.id === assignee);
      const res = await api.updateTodo(todo.id, {
        text: text.trim() || todo.text,
        description,
        due_date: dueDate,
        assignee_user_id: assignee || null,
        assignee_name: m?.name || "",
      });
      toast.success("Lagret");
      onChanged?.(res.data);
      setEditing(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Kunne ikke lagre");
    } finally { setSaving(false); }
  };

  const remove = async () => {
    if (!window.confirm("Slette denne oppgaven?")) return;
    try {
      await api.deleteTodo(todo.id);
      toast.success("Slettet");
      onChanged?.(null);
      onOpenChange(false);
    } catch (err) { toast.error(err.response?.data?.detail || "Kunne ikke slette"); }
  };

  const handleToggleDone = () => {
    onToggleDone?.(todo);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="todo-details-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: "'Cormorant Garamond', serif" }}>
            {todo.done && <Check className="h-5 w-5 text-emerald-600" />}
            <span className={todo.done ? "line-through text-stone-500" : ""}>
              {editing ? "Rediger oppgave" : "Oppgavedetaljer"}
            </span>
          </DialogTitle>
          <DialogDescription>
            {todo.done
              ? `Fullført ${formatDateTime(todo.completed_at)}${todo.completed_by_name ? ` av ${todo.completed_by_name}` : ""}`
              : "Les og oppdater detaljer, sett frist eller tildel."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Title */}
          <div>
            <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Tittel</label>
            {editing ? (
              <input
                value={text}
                onChange={(e) => setText(e.target.value)}
                className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500"
                data-testid="todo-details-title-input"
              />
            ) : (
              <p className={`text-lg mt-1 ${todo.done ? "line-through text-stone-400" : "text-stone-800"}`} data-testid="todo-details-title">
                {todo.text}
              </p>
            )}
          </div>

          {/* Description */}
          <div>
            <div className="flex items-center justify-between">
              <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide">Beskrivelse</label>
              {editing && (
                <SpellCheckButton
                  value={description}
                  onAccept={setDescription}
                  context="todo"
                  size="xs"
                  testid="todo-description-spell-btn"
                  label="AI staveretter"
                />
              )}
            </div>
            {editing ? (
              <textarea
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                placeholder="Hva skal gjøres? Legg til detaljer, fremgangsmåte, notater..."
                className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500 resize-none min-h-[120px]"
                data-testid="todo-details-description-input"
              />
            ) : todo.description ? (
              <p className="text-sm mt-1 text-stone-700 whitespace-pre-wrap" data-testid="todo-details-description">
                {todo.description}
              </p>
            ) : (
              <p className="text-sm mt-1 text-stone-400 italic">Ingen beskrivelse lagt til enda</p>
            )}
          </div>

          {/* Due date */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide flex items-center gap-1">
                <Calendar className="h-3 w-3" /> Frist
              </label>
              {editing ? (
                <input
                  type="date"
                  value={dueDate}
                  onChange={(e) => setDueDate(e.target.value)}
                  className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500"
                  data-testid="todo-details-due-input"
                />
              ) : (
                <p className={`text-sm mt-1 flex items-center gap-1 ${isOverdue ? "text-red-600 font-medium" : "text-stone-700"}`} data-testid="todo-details-due">
                  {todo.due_date ? (
                    <>
                      {isOverdue && <Clock className="h-3.5 w-3.5" />}
                      {formatDate(todo.due_date)}
                      {isOverdue && " (forfalt)"}
                    </>
                  ) : (
                    <span className="text-stone-400 italic">Ingen frist</span>
                  )}
                </p>
              )}
            </div>

            {/* Assignee */}
            <div>
              <label className="text-xs font-semibold text-stone-500 uppercase tracking-wide flex items-center gap-1">
                <User className="h-3 w-3" /> Tildelt
              </label>
              {editing && members.length > 0 ? (
                <select
                  value={assignee}
                  onChange={(e) => setAssignee(e.target.value)}
                  className="w-full mt-1 rounded-md border border-stone-200 bg-white px-3 py-2 text-sm outline-none focus:border-sky-500"
                  data-testid="todo-details-assignee-select"
                >
                  <option value="">Ingen</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.id}>{m.name}</option>
                  ))}
                </select>
              ) : (
                <p className="text-sm mt-1 text-stone-700">
                  {todo.assignee_name || members.find((m) => m.id === todo.assignee_user_id)?.name || <span className="text-stone-400 italic">Ingen</span>}
                </p>
              )}
            </div>
          </div>

          {/* Metadata footer */}
          {!editing && (
            <div className="border-t border-stone-100 pt-2 text-[11px] text-stone-400 space-y-0.5">
              <div>Opprettet {formatDateTime(todo.created_at)}{todo.created_by_name ? ` av ${todo.created_by_name}` : ""}</div>
            </div>
          )}

          {/* Actions */}
          <div className="flex items-center gap-2 pt-2">
            {editing ? (
              <>
                <button
                  onClick={save}
                  disabled={saving}
                  className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-sky-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-sky-700 transition-colors disabled:opacity-50"
                  data-testid="todo-details-save-btn"
                >
                  <Save className="h-4 w-4" /> {saving ? "Lagrer..." : "Lagre"}
                </button>
                <button
                  onClick={() => setEditing(false)}
                  className="rounded-md border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors"
                  data-testid="todo-details-cancel-btn"
                >
                  <X className="h-4 w-4" />
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleToggleDone}
                  className={`flex-1 inline-flex items-center justify-center gap-2 rounded-md px-4 py-2.5 text-sm font-semibold transition-colors ${
                    todo.done
                      ? "border border-stone-200 bg-white text-stone-700 hover:bg-stone-50"
                      : "bg-emerald-600 text-white hover:bg-emerald-700"
                  }`}
                  data-testid="todo-details-toggle-btn"
                >
                  <Check className="h-4 w-4" /> {todo.done ? "Marker som ikke gjort" : "Marker som ferdig"}
                </button>
                <button
                  onClick={() => setEditing(true)}
                  className="rounded-md border border-stone-200 bg-white px-3 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors inline-flex items-center gap-1"
                  data-testid="todo-details-edit-btn"
                >
                  <Pencil className="h-4 w-4" /> Rediger
                </button>
                <button
                  onClick={remove}
                  className="rounded-md border border-red-200 bg-white p-2.5 text-red-600 hover:bg-red-50 transition-colors"
                  data-testid="todo-details-delete-btn"
                  title="Slett oppgave"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
